import { useEffect, useState } from 'react';
import { FaGithub } from 'react-icons/fa';
import { VscVscode } from 'react-icons/vsc';
import { Outlet, useNavigate } from 'react-router-dom';
import { Button, Typography } from '@qodo/design-system';
import { JetBrainsIcon } from 'icons/JetBrains';
import { Loader } from '../../components/Loader';
import { MaintenanceAlert } from '../../components/MaintenanceAlert';
import { LOCAL_STORAGE_KEYS } from '../../consts/localStorage';
import { CONTACT_SUPPORT_URL } from '../../consts/url';
import { useMaintenanceMode } from '../../hooks/useMaintenanceMode';
import { QUERY_PARAMS, ROUTES } from '../../routes/consts';
import { cn } from '../../utils/className';
import { isAirgappedMode } from '../../utils/env';
import { fetchToken } from '../../utils/token';
import { spawn } from '../../utils/translations';
import {
  getQueryParam,
  isLoginPage,
  isSuccessSigninExternalClientPage,
  openUrl,
} from '../../utils/url';
import { LoginLogo } from './assets/LoginLogo';
import { ProviderRating } from './parts';

export const Login = () => {
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { isMaintenanceMode } = useMaintenanceMode();
  const isLoginMethodsPage =
    isLoginPage() && !isSuccessSigninExternalClientPage();

  const outletContainerClassName = cn(
    'flex flex-col items-center justify-center h-full w-[94%]',
    {
      'w-[50%]': isLoginMethodsPage,
      'w-fit': isSuccessSigninExternalClientPage(),
    },
  );

  const outletInnerContainerClassName = cn({
    'rounded-md py-10 px-8 w-full flex items-center':
      !isSuccessSigninExternalClientPage(),
  });

  const spawnFooter = spawn('login.footer');

  useEffect(() => {
    const redirectTo = getQueryParam(QUERY_PARAMS.REDIRECT_TO);

    if (redirectTo) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.REDIRECT_TO, redirectTo);
    }

    const code = getQueryParam(QUERY_PARAMS.CODE);

    const handleLogin = async () => {
      try {
        if (isAirgappedMode() && code) {
          await fetchToken();

          const url = new URL(window.location.href);

          url.searchParams.delete(QUERY_PARAMS.CODE);
          url.searchParams.delete(QUERY_PARAMS.STATE);
          window.history.replaceState({}, document.title, url.toString());
          setLoading(false);
          navigate(ROUTES.SUCCESS_SIGNIN_EXTERNAL_CLIENT);

          return;
        }

        setLoading(false);
        // eslint-disable-next-line
      } catch (error) {
        setLoading(false);
      }
    };

    handleLogin();
  }, []);

  const onSupportClick = () => {
    openUrl(CONTACT_SUPPORT_URL);
  };

  const renderSupportContent = () => {
    if (isAirgappedMode()) {
      return <span>{spawnFooter('contact-administrator')}</span>;
    }

    return (
      <Button variant="link" className="p-0" onClick={onSupportClick}>
        {spawnFooter('link')}
      </Button>
    );
  };

  if (loading) {
    return <Loader center />;
  }

  const spawnMarketing = spawn('login.marketing');

  return (
    <div className="h-screen flex flex-col">
      <div className="flex-1 relative flex">
        {/* Left Half - Marketing Content */}

        <div
          className={cn(
            'hidden lg:flex lg:flex-1 p-12 font-figtree relative min-h-132.5',
            { 'lg:hidden': isSuccessSigninExternalClientPage() },
          )}
        >
          <div
            className="absolute top-1/2 inset-x-0 flex justify-center"
            style={{ transform: 'translateY(calc(-100% - 220px))' }}
          >
            <LoginLogo />
          </div>
          <div className="absolute top-1/2 inset-x-0 -translate-y-1/2 flex justify-center px-12">
            <div className="max-w-xl w-full text-white flex flex-col gap-6 items-center">
              <Typography
                variant="h2"
                className="text-[40px] leading-[120%] tracking-[-0.025rem] text-center"
              >
                {spawnMarketing('title')}
              </Typography>
              <p className="text-text-bg-neutral-mid text-center text-lg font-light leading-[150%] tracking-[0.36px] max-w-120">
                {spawnMarketing('description')}
              </p>
            </div>
          </div>
          <div className="absolute bottom-24 inset-x-0 flex justify-center">
            <div className="flex flex-col gap-2 items-center">
              <div className="flex gap-6">
                <ProviderRating rating="883600">
                  <VscVscode className="size-5 text-white" />
                </ProviderRating>
                <ProviderRating rating="638000">
                  <JetBrainsIcon className="size-5 text-white" />
                </ProviderRating>
                <ProviderRating rating="11900">
                  <FaGithub className="size-5 text-white" />
                </ProviderRating>
              </div>
              <p className="text-xs leading-5 text-purple-muted-300 opacity-90 font-mono text-center">
                {spawnMarketing('stats').toUpperCase()}
              </p>
            </div>
          </div>
        </div>

        {/* Right Half - Login Methods */}
        <div className="flex-1 flex flex-col relative">
          <div className="flex-1 flex justify-center">
            <div className={outletContainerClassName}>
              {isMaintenanceMode && <MaintenanceAlert className="top-32" />}
              <div className="h-full flex flex-col items-center justify-center gap-12">
                <div className={outletInnerContainerClassName}>
                  <Outlet />
                </div>
              </div>
            </div>
          </div>
          <footer className="absolute bottom-14 inset-x-0 p-10 flex justify-center">
            <div className="text-sm whitespace-nowrap text-center text-purple-muted-400 leading-normal tracking-[0.0075rem]">
              <span>{spawnFooter('text')}</span> &nbsp;
              {renderSupportContent()}
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
};
